import React, { useEffect, useState, useContext } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  Grid,
  Chip,
  Divider,
  Tooltip,
  Typography,
  Collapse,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { addDays } from 'date-fns';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SaveIcon from '@mui/icons-material/Save';
import { useGenerateTasksForHarvest } from '../utils/harvestTaskGenerator';
import {
  collection,
  addDoc,
  updateDoc,
  getDocs,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { AppContext } from '../AppContext';
import ConfirmationDialog from '../components/ConfirmationDialog';

import { trackFeature } from '../utils/analytics';

function AddHarvest({
  open,
  onClose,
  onAdd,
  editMode = false,
  harvestToEdit = {},
  setSuccess,
  setError,
}) {
  const { user, seeds, trays, media, solutions, userData, farmData } = useContext(AppContext);
  const activeFarmId = userData?.activeFarmId;
  const [templateConfirmOpen, setTemplateConfirmOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const generateTasksForHarvest = useGenerateTasksForHarvest();

  // Filter out archived seeds for the dropdown.
  const activeSeeds = seeds.filter((seed) => !seed.archived);
  const defaultDateFormat = farmData?.settings?.dateFormat || 'dd-MM-yyyy';
  const defaultWeightUnit = farmData?.settings?.weightUnit || 'g';
  const defaultYieldUnit =
    farmData?.settings?.yieldUnitDefault || farmData?.settings?.yieldUnit || defaultWeightUnit;
  const defaultPortionUnit =
    farmData?.settings?.portionUnitDefault || farmData?.settings?.portionUnit || defaultWeightUnit;
  const defaultDensityUnit =
    farmData?.settings?.densityUnitDefault || farmData?.settings?.densityUnit || defaultWeightUnit;

  // New state with seed-related fields top-level and new yield fields.
  const [newHarvest, setNewHarvest] = useState({
    startDate: null,
    seedId: '',
    sowDensityValue: '',
    sowDensityUnit: defaultDensityUnit,
    growth: '',
    mediaId: '',
    solutionId: '',
    trayId: '',
    trayNumber: '',
    // New fields replacing old yield and portions:
    yieldValue: '',
    portionSize: '',
    yieldUnit: defaultYieldUnit,
    portionUnit: defaultPortionUnit,
    portions: '',       // computed
    portionsLeft: '',   // initialize same as portions
    shelfLife: '',
    harvestBuffer: '',
    notes: '',
    recurring: false,
    recurringUntil: null,
  });

  const [touched, setTouched] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const canSaveTemplate =
    newHarvest.seedId &&
    newHarvest.startDate &&
    newHarvest.yieldValue &&
    newHarvest.portionSize;

  const getUnitField = (data, objectKey, legacyValueKey, legacyUnitKey, fallbackUnit) => {
    const obj = data?.[objectKey];
    if (obj && typeof obj === 'object') {
      return {
        value: obj.value ?? '',
        unit: obj.unit || fallbackUnit,
      };
    }
    return {
      value: data?.[legacyValueKey] ?? '',
      unit: data?.[legacyUnitKey] || fallbackUnit,
    };
  };



  // Listen for templates.
  useEffect(() => {
    if (!open || !user?.uid || !activeFarmId) {
      setTemplates([]);
      return;
    }
    const templatesRef = collection(db, 'farms', activeFarmId, 'harvestTemplates');
    const unsubscribe = onSnapshot(templatesRef, (snapshot) => {
      const loadedTemplates = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTemplates(loadedTemplates);
    });
    return () => unsubscribe();
  }, [open, user, activeFarmId]);

  // Prefill state when editing.
  useEffect(() => {
    if (!open) return;
    if (editMode && harvestToEdit.id) {
      const parsedDate = harvestToEdit.startDate
        ? new Date(harvestToEdit.startDate)
        : null;
      const yieldField = getUnitField(
        harvestToEdit,
        'estimatedYield',
        'yieldValue',
        'yieldUnit',
        defaultYieldUnit
      );
      const portionField = getUnitField(
        harvestToEdit,
        'portionSize',
        'portionSize',
        'portionUnit',
        defaultPortionUnit
      );
      const densityField = getUnitField(
        harvestToEdit,
        'sowDensity',
        'sowDensityValue',
        'sowDensityUnit',
        defaultDensityUnit
      );

      setNewHarvest({
        startDate: parsedDate,
        seedId: harvestToEdit.seedId || '',
        sowDensityValue: densityField.value,
        sowDensityUnit: densityField.unit,
        growth: harvestToEdit.growth || '',
        mediaId: harvestToEdit.mediaId || '',
        solutionId: harvestToEdit.solutionId || '',
        trayId: harvestToEdit.trayId || '',
        trayNumber: harvestToEdit.trayNumber || '',
        yieldValue: yieldField.value,
        portionSize: portionField.value,
        yieldUnit: yieldField.unit,
        portionUnit: portionField.unit,
        portions: harvestToEdit.portions || '',
        portionsLeft: harvestToEdit.portionsLeft || '',       // load previous
        shelfLife: harvestToEdit.shelfLife || '',
        harvestBuffer: harvestToEdit.harvestBuffer || '',
        notes: harvestToEdit.notes || '',
      });
    } else {
      setNewHarvest({
        startDate: null,
        seedId: '',
        sowDensityValue: '',
        sowDensityUnit: defaultDensityUnit,
        growth: '',
        mediaId: '',
        solutionId: '',
        trayId: '',
        trayNumber: '',
        yieldValue: '',
        portionSize: '',
        yieldUnit: defaultYieldUnit,
        portionUnit: defaultPortionUnit,
        portions: '',
        portionsLeft: '',
        shelfLife: '',
        harvestBuffer: '',
        notes: '',
      });
    }
    setTouched({});
    setFormSubmitted(false);
  }, [open, editMode, harvestToEdit, defaultDensityUnit, defaultPortionUnit, defaultYieldUnit]);

  const handleBlur = (field) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const isError = (field) => {
    if (field === 'seed') {
      return !newHarvest.seedId && (touched.seed || formSubmitted);
    }
    return !newHarvest[field] && (touched[field] || formSubmitted);
  };


  const isRecurringError = (subField) => {
    if (!newHarvest.recurring) return false;
    const hasValue = Boolean(newHarvest.recurring[subField]);
    return !hasValue && (touched[subField] || formSubmitted);
  };


  async function handleSave() {
    setFormSubmitted(true);

    // Required validation
    if (
      !newHarvest.startDate ||
      !newHarvest.seedId ||
      !newHarvest.trayNumber ||
      !newHarvest.yieldValue ||
      !newHarvest.portionSize
    ) {
      setError?.(
        'Please fill in required fields: Start Date, Seed, Tray Number, Estimated Yield and Portion Size.'
      );
      return;
    }
    if (!user?.uid) {
      setError?.('User not logged in.');
      return;
    }
    if (!activeFarmId) {
      setError?.('No active farm selected.');
      return;
    }

    try {
      // Lookup seed for name and defaults
      const seedData = seeds.find((s) => s.id === newHarvest.seedId) || {};
      const seedName = seedData.name || 'Unknown';

      // Compute harvestDate
      const growthDays = Number(newHarvest.growth) || 0;
      const harvestDateObj = addDays(newHarvest.startDate, growthDays);
      const isoHarvestDate = harvestDateObj.toISOString();

      // Parse inputs
      const yieldValueNum = parseFloat(newHarvest.yieldValue) || 0;
      const portionSizeNum = parseFloat(newHarvest.portionSize) || 1;
      const sowDensityNum = parseFloat(newHarvest.sowDensityValue) || 0;
      const perTrayYield = yieldValueNum;  // yieldValue
      const portionSize = portionSizeNum;  // portionSize
      const trayCount = parseInt(newHarvest.trayNumber, 10) || 1;  // trayNumber

      // Compute totalYield across all trays
      const totalYieldValue = Math.round(perTrayYield * trayCount * 100) / 100;

      // Compute how many portions you get out of that total
      const rawPortions = totalYieldValue / portionSize;
      const calculatedPortions = Math.round(rawPortions * 10) / 10;

      // Build payload
      const payload = {
        startDate: newHarvest.startDate.toISOString(),
        harvestDate: isoHarvestDate,
        seedId: newHarvest.seedId,
        sowDensityValue: sowDensityNum,
        sowDensityUnit: newHarvest.sowDensityUnit,
        growth: parseInt(newHarvest.growth) || 0,
        mediaId: newHarvest.mediaId || '',
        solutionId: newHarvest.solutionId || '',
        trayId: newHarvest.trayId,
        trayNumber: parseInt(newHarvest.trayNumber) || 0,
        yieldValue: yieldValueNum,
        yieldUnit: newHarvest.yieldUnit,
        portionSize: {
          value: portionSizeNum,
          unit: newHarvest.portionUnit,
        },
        sowDensity: {
          value: sowDensityNum,
          unit: newHarvest.sowDensityUnit,
        },
        estimatedYield: {
          value: yieldValueNum,
          unit: newHarvest.yieldUnit,
        },
        portionUnit: newHarvest.portionUnit,
        portions: calculatedPortions,
        portionsLeft: calculatedPortions,
        totalYieldValue: totalYieldValue,
        shelfLife: newHarvest.shelfLife
          ? (newHarvest.shelfLife, 10)
          : null,
        harvestBuffer: newHarvest.harvestBuffer
          ? (newHarvest.harvestBuffer, 10)
          : null,
        notes: newHarvest.notes,
        recurring: Boolean(newHarvest.recurring),
        frequency: typeof newHarvest.recurring === 'object'
          ? parseInt(newHarvest.recurring.frequency, 10)
          : null,
        period: typeof newHarvest.recurring === 'object'
          ? newHarvest.recurring.period
          : null,
        recurringUntil: newHarvest.recurringUntil
          ? newHarvest.recurringUntil.toISOString()
          : null,
        nextRun: newHarvest.recurring
          ? newHarvest.startDate.toISOString()
          : null,
      };

      let harvestId = '';
      if (editMode && harvestToEdit.id) {
        // 1) Update existing harvest
        await updateDoc(
          doc(db, 'farms', activeFarmId, 'harvests', harvestToEdit.id),
          payload
        );
        harvestId = harvestToEdit.id;

        // 2) Remove old tasks for that harvest
        const q = query(
          collection(db, 'farms', activeFarmId, 'tasks'),
          where('harvestId', '==', harvestId)
        );
        const snap = await getDocs(q);
        await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));

        trackFeature('harvest_updated', {
          harvestId,
          seedId: newHarvest.seedId,
          portions: payload.portions,
        });

        setSuccess?.('Harvest updated successfully.');
      } else {
        // Create new harvest
        const ref = await addDoc(
          collection(db, 'farms', activeFarmId, 'harvests'),
          payload
        );
        harvestId = ref.id;
        trackFeature('harvest_saved', {
          harvestId,
          seedId: newHarvest.seedId,
          portions: payload.portions,
        });
        setSuccess?.('Harvest added successfully.');
      }

      // 3) Generate and save new tasks
      const tasks = generateTasksForHarvest({
        harvestId,
        seedName,
        startDate: newHarvest.startDate.toISOString(),
        germinationDays: Number(seedData.germination) || 0,
        blackoutDays: Number(seedData.blackout) || 0,
        growthDays,
        wateringFrequency: Number(seedData.wateringFrequency) || 1,
        wateringIntervalUnit: seedData.wateringIntervalUnit || 'days',
        dryDay: Boolean(seedData.dryDay),
      });
      const tasksRef = collection(db, 'farms', activeFarmId, 'tasks');
      await Promise.all(tasks.map((t) => addDoc(tasksRef, t)));

      if (onAdd) onAdd(payload);
      handleCloseDialog();
    } catch (err) {
      console.error(err);
      setError?.('Error saving harvest.');
    }
  }

  function handleCloseDialog() {
    setFormSubmitted(false);
    setTouched({});
    onClose();
  }

  const handleDialogKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCloseDialog();
      return;
    }

    if (e.key !== 'Enter') return;
    if (e.defaultPrevented || e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;

    const target = e.target;
    const tag = target?.tagName?.toLowerCase();
    if (tag === 'textarea' || target?.isContentEditable) return;
    if (target?.closest?.('[role="listbox"], [role="menu"]')) return;

    e.preventDefault();
    handleSave();
  };

  // Template saving
  const handleOpenTemplateConfirm = () => setTemplateConfirmOpen(true);
  const handleConfirmSaveTemplate = async (inputValue) => {
    if (!inputValue.trim()) return;
    try {
      if (!user?.uid) {
        setError?.('User not logged in.');
        return;
      }
      if (!activeFarmId) {
        setError?.('No active farm selected.');
        return;
      }
      // Copy everything except IDs & startDate
      const { id, startDate, ...templateData } = newHarvest;
      const yieldValueNum = parseFloat(newHarvest.yieldValue) || 0;
      const portionSizeNum = parseFloat(newHarvest.portionSize) || 0;
      const sowDensityNum = parseFloat(newHarvest.sowDensityValue) || 0;
      const payload = {
        ...templateData,
        estimatedYield: { value: yieldValueNum, unit: newHarvest.yieldUnit },
        portionSize: { value: portionSizeNum, unit: newHarvest.portionUnit },
        sowDensity: { value: sowDensityNum, unit: newHarvest.sowDensityUnit },
        templateName: inputValue.trim(),
      };
      await addDoc(
        collection(db, 'farms', activeFarmId, 'harvestTemplates'),
        payload
      );
      trackFeature('harvest_template_saved', {
        templateName: inputValue.trim(),
      });
      setSuccess?.('Template saved successfully');
      setTemplateConfirmOpen(false);
      setTemplateName('');
    } catch (err) {
      setError?.('Error saving template.');
    }
  };

  const handleCancelSaveTemplate = () => {
    setTemplateConfirmOpen(false);
    setTemplateName('');
  };

  // Load/delete templates
  const loadTemplate = (tmpl) => {
    trackFeature('harvest_template_loaded', {
      templateId: tmpl.id,
      templateName: tmpl.templateName,
    });
    const { startDate, id, ...data } = tmpl;
    const yieldField = getUnitField(
      data,
      'estimatedYield',
      'yieldValue',
      'yieldUnit',
      defaultYieldUnit
    );
    const portionField = getUnitField(
      data,
      'portionSize',
      'portionSize',
      'portionUnit',
      defaultPortionUnit
    );
    const densityField = getUnitField(
      data,
      'sowDensity',
      'sowDensityValue',
      'sowDensityUnit',
      defaultDensityUnit
    );
    setNewHarvest({
      ...data,
      startDate: startDate ? new Date(startDate) : null,
      sowDensityValue: densityField.value,
      sowDensityUnit: densityField.unit,
      yieldValue: yieldField.value,
      yieldUnit: yieldField.unit,
      portionSize: portionField.value,
      portionUnit: portionField.unit,
    });
  };
  const handleDeleteTemplate = async (tmplId) => {

    try {
      if (!user?.uid || !activeFarmId) return;
      await deleteDoc(
        doc(db, 'farms', activeFarmId, 'harvestTemplates', tmplId)
      );
      trackFeature('harvest_template_deleted', { templateId: tmplId });
      setTemplates((prev) => prev.filter((t) => t.id !== tmplId));
      setSuccess?.('Template deleted.');
    } catch (err) {
      setError?.('Error deleting template.');
    }
  };

  const unitOptions = ['g', 'oz', 'lb', 'kg'];
  const renderUnitSelect = (value, onChange) => (
    <Select
      value={value}
      onChange={onChange}
      variant="standard"
      disableUnderline
      sx={{
        fontSize: '0.85rem',
        ml: 1,
        opacity: 0.85,
        '&:hover': { opacity: 1 },
        '&.Mui-focused': { opacity: 1 },
        '& .MuiSelect-select': { paddingRight: '20px !important' },
        '& .MuiSelect-icon': { fontSize: 18 },
      }}
    >
      {unitOptions.map((opt) => (
        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
      ))}
    </Select>
  );

  return (
    <Dialog open={open} onClose={handleCloseDialog} onKeyDown={handleDialogKeyDown} fullWidth>
      <DialogTitle>
        {editMode ? (
          <>
            Edit Harvest{' '}
            <Typography component="span" sx={{ fontSize: '0.8em', ml: 1 }}>
              (recreates tasks)
            </Typography>
          </>
        ) : (
          'Add New Harvest'
        )}
      </DialogTitle>
      <DialogContent>
        {/* Template Chips */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {templates.map((tmpl) => (
            <Chip
              key={tmpl.id}
              label={tmpl.templateName}
              onClick={() => loadTemplate(tmpl)}
              onDelete={() => handleDeleteTemplate(tmpl.id)}
            />
          ))}
        </Box>

        <Grid container spacing={1} alignItems="center">
          {/* Seed */}
          <Grid item xs={12}>
            <FormControl
              fullWidth
              margin="dense"
              required
              error={isError('seed')}
            >
              <InputLabel>Seed</InputLabel>
              <Select
                label="Seed"
                value={newHarvest.seedId}
                onChange={(e) =>
                  setNewHarvest((p) => ({ ...p, seedId: e.target.value }))
                }
                onBlur={() => handleBlur('seed')}
              >
                {activeSeeds.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
              {isError('seed') && (
                <FormHelperText>This field is required</FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* Tray + number */}
          <Grid item xs={12} sm={8}>
            <FormControl
              fullWidth
              margin="dense"
              required
              error={isError('trayId')}
            >
              <InputLabel>Tray</InputLabel>
              <Select
                label="Tray"
                value={newHarvest.trayId}
                onChange={(e) =>
                  setNewHarvest((p) => ({ ...p, trayId: e.target.value }))
                }
                onBlur={() => handleBlur('trayId')}
              >
                {trays.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.brand} - {t.length}
                    {t.lengthUnit} x {t.width}
                    {t.widthUnit}
                  </MenuItem>
                ))}
              </Select>
              {isError('trayId') && (
                <FormHelperText>This field is required</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Tray Number"
              fullWidth
              required
              margin="dense"
              value={newHarvest.trayNumber}
              onChange={(e) =>
                setNewHarvest((p) => ({
                  ...p,
                  trayNumber: e.target.value.replace(/\D/g, ''),
                }))
              }
              onBlur={() => handleBlur('trayNumber')}
              error={isError('trayNumber')}
              helperText={isError('trayNumber') ? 'Required' : ''}
            />
          </Grid>

          {/* Start Date & Growth */}
          <Grid item xs={12} sm={7}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={newHarvest.startDate}
                onChange={(v) =>
                  setNewHarvest((p) => ({ ...p, startDate: v }))
                }
                inputFormat={defaultDateFormat}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    margin="dense"
                    required
                    onBlur={() => handleBlur('startDate')}
                    error={isError('startDate')}
                    helperText={
                      isError('startDate') ? 'Required' : ''
                    }
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Growing (days)"
              fullWidth
              required
              margin="dense"
              value={newHarvest.growth}
              onChange={(e) =>
                setNewHarvest((p) => ({
                  ...p,
                  growth: e.target.value.replace(/\D/g, ''),
                }))
              }
              onBlur={() => handleBlur('growth')}
              error={isError('growth')}
              helperText={isError('growth') ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={12} sm={1}>
            <Tooltip title="How many days from sow to harvest.">
              <HelpOutlineIcon sx={{ cursor: 'pointer', mt: { xs: 1, sm: 2 } }} />
            </Tooltip>
          </Grid>

          {/* Estimated Yield */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Estimated Yield"
              fullWidth
              required
              margin="dense"
              value={newHarvest.yieldValue}
              onChange={(e) =>
                setNewHarvest((p) => ({
                  ...p,
                  yieldValue: e.target.value.replace(/[^\d.]/g, ''),
                }))
              }
              onBlur={() => handleBlur('yieldValue')}
              error={
                !newHarvest.yieldValue &&
                (touched.yieldValue || formSubmitted)
              }
              helperText={
                !newHarvest.yieldValue &&
                  (touched.yieldValue || formSubmitted)
                  ? 'Required'
                  : ''
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {renderUnitSelect(newHarvest.yieldUnit, (e) =>
                      setNewHarvest((p) => ({
                        ...p,
                        yieldUnit: e.target.value,
                      }))
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Portion Size */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Portion Size"
              fullWidth
              required
              margin="dense"
              value={newHarvest.portionSize}
              onChange={(e) =>
                setNewHarvest((p) => ({
                  ...p,
                  portionSize: e.target.value.replace(/[^\d.]/g, ''),
                }))
              }
              onBlur={() => handleBlur('portionSize')}
              error={
                !newHarvest.portionSize &&
                (touched.portionSize || formSubmitted)
              }
              helperText={
                !newHarvest.portionSize &&
                  (touched.portionSize || formSubmitted)
                  ? 'Required'
                  : ''
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {renderUnitSelect(newHarvest.portionUnit, (e) =>
                      setNewHarvest((p) => ({
                        ...p,
                        portionUnit: e.target.value,
                      }))
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box
              onClick={() => setShowAdvanced((v) => !v)}
              role="button"
              tabIndex={0}
              aria-expanded={showAdvanced}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setShowAdvanced((v) => !v);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                cursor: 'pointer',
                userSelect: 'none',
                px: 2,
                py: 1.5,
                borderRadius: 1.5,
                backgroundColor: 'action.hover',
                '&:hover': { backgroundColor: 'action.selected' },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Advanced Growing Details
                </Typography>
                {!showAdvanced && (
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Sow density, medium, solution, shelf life, buffers, notes
                  </Typography>
                )}
              </Box>
              <KeyboardArrowDownIcon
                sx={{
                  transition: 'transform 150ms ease',
                  transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </Box>
          </Grid>
        </Grid>

        <Collapse in={showAdvanced}>
          <Divider sx={{ my: 1.5, opacity: 0.2 }} />
          <Grid container spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            {/* Sow Density + Shelf Life */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sow Density"
                fullWidth
                margin="dense"
                value={newHarvest.sowDensityValue}
                onChange={(e) =>
                  setNewHarvest((p) => ({
                    ...p,
                    sowDensityValue: e.target.value.replace(/[^\d.]/g, ''),
                  }))
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {renderUnitSelect(newHarvest.sowDensityUnit, (e) =>
                        setNewHarvest((p) => ({
                          ...p,
                          sowDensityUnit: e.target.value,
                        }))
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Shelf Life (days)"
                fullWidth
                margin="dense"
                value={newHarvest.shelfLife}
                onChange={(e) =>
                  setNewHarvest((p) => ({
                    ...p,
                    shelfLife: e.target.value.replace(/\D/g, ''),
                  }))
                }
              />
            </Grid>

            {/* Medium + Solution */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Medium</InputLabel>
                <Select
                  label="Medium"
                  value={newHarvest.mediaId}
                  onChange={(e) =>
                    setNewHarvest((p) => ({ ...p, mediaId: e.target.value }))
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  {(media || []).map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.brand}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Solution</InputLabel>
                <Select
                  label="Solution"
                  value={newHarvest.solutionId}
                  onChange={(e) =>
                    setNewHarvest((p) => ({ ...p, solutionId: e.target.value }))
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  {(solutions || []).map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Harvest Buffer */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Harvest Buffer (days)"
                fullWidth
                margin="dense"
                value={newHarvest.harvestBuffer}
                onChange={(e) =>
                  setNewHarvest((p) => ({
                    ...p,
                    harvestBuffer: e.target.value.replace(/\D/g, ''),
                  }))
                }
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                minRows={2}
                margin="dense"
                value={newHarvest.notes}
                onChange={(e) =>
                  setNewHarvest((p) => ({ ...p, notes: e.target.value }))
                }
              />
            </Grid>
          </Grid>
        </Collapse>

        {/* Actions */}
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Tooltip title={!canSaveTemplate ? 'Fill required fields to save preset' : ''}>
            <span>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<SaveIcon />}
                onClick={handleOpenTemplateConfirm}
                disabled={!canSaveTemplate}
              >
                Save Preset
              </Button>
            </span>
          </Tooltip>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="text" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button variant="contained" color="secondary" onClick={handleSave}>
              {editMode ? 'Update' : 'Add'}
            </Button>
          </Box>

        </Box>

        {/* ConfirmationDialog for saving template */}
        <ConfirmationDialog
          open={templateConfirmOpen}
          title="Save as template?"
          message="Enter a nickname for this harvest template"
          showInput={true}
          onConfirm={handleConfirmSaveTemplate}
          onClose={handleCancelSaveTemplate}
        >
          <TextField
            fullWidth
            label="Template Name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            autoFocus
            sx={{ mt: 2 }}
          />
        </ConfirmationDialog>
      </DialogContent>
    </Dialog>
  );
}

export default AddHarvest;








